/**
 * Trailblazers game-design bundle — the file an admin downloads to reuse or
 * share a game's design, and uploads to bring parts of one into another game.
 *
 * The bundle is a *menu*, not a snapshot: the admin ticks which SECTIONS to
 * export, and only those are written. On import the file's own `sections` keys
 * are the list of what can be brought in, so the importer never has to guess
 * and never offers something the file doesn't carry.
 *
 * Nothing binary or install-specific is embedded. Content-library images travel
 * as `imageIds`, with a display name carried alongside purely so the importer can
 * *name* the ones the target install is missing. Cartel rewards travel as
 * platform `itemId`s, which are the same on every install (version 2; a version 1
 * bundle carried install-specific `cartelItemId`s instead). The SERVER-ONLY answer coordinates on a
 * {@link TrlImageAsset} never enter a bundle. This mirrors the rule already set
 * out for content-library transfer in `./content-transfer`.
 *
 * Every section maps onto machinery that already exists: `map` onto
 * `TrlSaveMapRequest`, and each config section onto its own
 * `TRL_UPDATE_*_CONFIG` socket event — whose handler runs the matching
 * `normalizeTrl*Config` sanitiser. So an imported bundle is validated by the
 * same code that validates hand-authored settings; only the envelope below
 * needs checking client-side.
 *
 * Prefix: "TrlBundle"
 */
import { TRL_SABOTAGE_TYPES, TRL_SABOTAGE_DEFINITIONS } from './trlSabotage';
/**
 * File marker written into every bundle so the importer can reject unrelated
 * JSON (including the superseded single-map `swtorbit-trl-map` files).
 */
export const TRL_BUNDLE_KIND = 'swtorbit-trl-bundle';
/**
 * Bump when the bundle shape changes incompatibly. Importers accept every version
 * from 1 up to this one.
 *
 * - 1: cartel rewards reference the source install's `cartel_items` (`cartelItemId`).
 * - 2: cartel rewards reference platform items (`itemId`), the same on every install.
 */
export const TRL_BUNDLE_VERSION = 2;
/**
 * Every design item a bundle can carry. Order is the order both the export and
 * the import checklists are rendered in.
 */
export const TRL_BUNDLE_SECTIONS = [
    'map',
    'treasure',
    'triangulation',
    'stalemate',
    'radar',
    'disruption',
    'sabotage',
    'teamNames',
];
/** Admin-facing section names. Match the Game Settings tab labels. */
export const TRL_BUNDLE_SECTION_LABELS = {
    map: 'Galaxy Map',
    treasure: 'Treasure List',
    triangulation: 'Triangulation',
    stalemate: 'Stalemate Clues',
    radar: 'Radar Ranges',
    disruption: 'Disruption',
    sabotage: 'Sabotage',
    teamNames: 'Team Names',
};
/** One line under each checkbox saying what the section is for. */
export const TRL_BUNDLE_SECTION_HINTS = {
    map: 'Planets, paths, backdrop and per-planet treasure placement',
    treasure: 'The reward pool and how it spreads over the rounds',
    triangulation: 'How a 3-screenshot planet is completed',
    stalemate: 'The clue ladder that nudges a stalled team',
    radar: 'The distance bands every dig is scored against',
    disruption: 'Whether enemy Sparkle Powder can block a scanner',
    sabotage: 'Which pre-game planet sabotages teams may place',
    teamNames: 'The core names switched off and the custom ones added',
};
/** Plural-aware "3 planets" / "1 planet". */
function count(n, singular, plural = `${singular}s`) {
    return `${n} ${n === 1 ? singular : plural}`;
}
/**
 * A short "12 planets · 15 paths · backdrop" line describing what a section
 * holds. Shared by the export and import checklists so the admin reads the same
 * description on both sides of the round trip.
 *
 * Returns an empty string for a section the bundle doesn't carry.
 */
export function summarizeTrlBundleSection(id, sections) {
    switch (id) {
        case 'map': {
            const s = sections.map;
            if (!s)
                return '';
            const parts = [count(s.nodes.length, 'planet'), count(s.edges.length, 'path')];
            const placed = s.nodes.filter((n) => (n.treasureItemIds?.length ?? 0) > 0).length;
            if (placed > 0)
                parts.push(`${count(placed, 'planet')} with treasure`);
            if (s.background?.image || s.background?.color)
                parts.push('backdrop');
            return parts.join(' · ');
        }
        case 'treasure': {
            const s = sections.treasure;
            if (!s)
                return '';
            const cartel = s.config.items.filter((i) => i.kind === 'cartel').length;
            const parts = [count(s.config.items.length, 'reward')];
            if (cartel > 0)
                parts.push(`${count(cartel, 'SWTOR item')}`);
            return parts.join(' · ');
        }
        case 'triangulation': {
            const s = sections.triangulation;
            if (!s)
                return '';
            return s.config.simultaneousBroadcast
                ? `Simultaneous broadcast · ${s.config.broadcastWindowSeconds}s window`
                : 'Broadcast one at a time';
        }
        case 'stalemate': {
            const s = sections.stalemate;
            if (!s)
                return '';
            const on = [
                s.config.area.enabled && 'area',
                s.config.scanner.enabled && 'scanner',
                s.config.pinpoint.enabled && 'pinpoint',
            ].filter((v) => typeof v === 'string');
            return on.length > 0 ? `Clues: ${on.join(', ')}` : 'All clues off';
        }
        case 'radar': {
            const s = sections.radar;
            if (!s)
                return '';
            return count(s.config.ranges.length, 'band');
        }
        case 'disruption': {
            const s = sections.disruption;
            if (!s)
                return '';
            return s.config.enabled ? 'Enabled' : 'Disabled';
        }
        case 'sabotage': {
            const s = sections.sabotage;
            if (!s)
                return '';
            const on = TRL_SABOTAGE_TYPES.filter((t) => s.config.types[t].enabled).map((t) => TRL_SABOTAGE_DEFINITIONS[t].label);
            return on.length > 0 ? `Enabled: ${on.join(', ')}` : 'All sabotages off';
        }
        case 'teamNames': {
            const s = sections.teamNames;
            if (!s)
                return '';
            const parts = [];
            if (s.config.customNames.length > 0)
                parts.push(count(s.config.customNames.length, 'custom name'));
            if (s.config.disabledCoreNames.length > 0) {
                parts.push(`${s.config.disabledCoreNames.length} core name(s) switched off`);
            }
            return parts.length > 0 ? parts.join(' · ') : 'Default name list';
        }
    }
}
